import { signal } from '@preact/signals';
import { projects, createProject, deleteProject, activeProjectId, setActiveProject } from '../stores/project.store';
import { ProjectContext } from '../components/ProjectContext';
import type { Project } from '@elon-tools/shared';
import { T } from '../styles/tokens';
import { Button , Input} from '../components/ui';

const showModal = signal(false);
const newDomain = signal('');
const newName = signal('');
const newNiche = signal('');
const creating = signal(false);
const error = signal('');

async function handleCreate(e: Event) {
  e.preventDefault();
  creating.value = true;
  error.value = '';
  try {
    await createProject(newDomain.value, newName.value || undefined, newNiche.value || undefined);
    showModal.value = false;
    newDomain.value = '';
    newName.value = '';
    newNiche.value = '';
  } catch (err) {
    error.value = (err as Error).message;
  } finally {
    creating.value = false;
  }
}

function ProjectCard({ project }: { project: Project }) {
  const isActive = activeProjectId.value === project.id;
  const meta = project.metadata as Record<string, unknown>;

  return (
    <div class="card" style={{ borderColor: isActive ? T.primary : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        {project.favicon_url && (
          <img src={project.favicon_url} alt="" style={{ width: 28, height: 28, borderRadius: 4 }}
            onError={(e) => (e.currentTarget.style.display = 'none')} />
        )}
        <div>
          <strong>{project.name ?? project.domain}</strong>
          <div style={{ fontSize: 12, color: T.textMuted }}>{project.domain}</div>
        </div>
        {isActive && <span class="badge badge-info" style={{ marginLeft: 'auto' }}>Ativo</span>}
      </div>

      {project.niche && <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}>Nicho: {project.niche}</div>}
      
      {Array.isArray(meta?.tech_hints) && meta.tech_hints.length > 0 && (
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
          Tech: {(meta.tech_hints as string[]).slice(0, 5).join(', ')}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        {!isActive && (
          <Button variant="primary" size="sm" onClick={() => setActiveProject(project.id)}>Ativar</Button>
        )}
        <Button variant="danger" size="sm" onClick={() => { if (confirm('Deletar projeto?')) deleteProject(project.id); }}>
          Deletar
        </Button>
      </div>
    </div>
  );
}

export function ProjectsPage() {
  return (
    <div>
      <div class="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
 <h1>Projetos</h1>
          <p>{projects.value.length} projeto(s)</p>
        </div>
        <Button onClick={() => (showModal.value = true)}>+ Adicionar Projeto</Button>
      </div>

      {projects.value.length === 0 ? (
        <div class="empty-state">
 <div class="icon"></div>
          <h3>Nenhum projeto</h3>
          <p>Crie seu primeiro projeto para começar a usar os agentes.</p>
          <Button style={{ marginTop: 16 }} onClick={() => (showModal.value = true)}>
            + Adicionar Projeto
          </Button>
        </div>
      ) : (
        <div class="card-grid">
          {projects.value.map((p) => <ProjectCard key={p.id} project={p} />)}
        </div>
      )}

      {showModal.value && (
        <div class="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) showModal.value = false; }}>
          <div class="modal">
            <h2>Adicionar Projeto</h2>
            {error.value && <div class="error-banner">{error.value}</div>}
            <form onSubmit={handleCreate}>
              <div class="form-group">
                <label class="label">Domínio *</label>
                <Input value={newDomain.value} onChange={(v) => (newDomain.value = v)} placeholder="example.com" />
              </div>
              <div class="form-group">
                <label class="label">Nome (opcional)</label>
                <Input value={newName.value} onChange={(v) => (newName.value = v)} placeholder="Meu Site" />
              </div>
              <div class="form-group">
                <label class="label">Nicho (opcional)</label>
                <Input value={newNiche.value} onChange={(v) => (newNiche.value = v)} placeholder="E-commerce, SaaS, Blog..." />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" type="button" onClick={() => (showModal.value = false)}>Cancelar</Button>
                <Button type="submit" disabled={creating.value} loading={creating.value}>
                  {creating.value ? 'Criando...' : 'Criar Projeto'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
